import React from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ThemeProvider } from '@mui/material/styles';

import DrugAdministrationGrid from "../../components/DrugAdministrationGrid";
import PrimaryMonitoringGrid from "../../components/PrimaryMonitoringGrid";
import AdvancedMonitoringGrid from "../../components/AdvancedMonitoringGrid";
import DraftSaveButton from '../../../components/DraftSaveButton';
import SignatureDialog from './components/SignatureDialog';

import theme from '../../../theme/theme.js';
import { DRAFT_KEYS } from '../../../utils/draftUtils';

import { useAnesthesiaForm } from './hooks/useAnesthesiaForm';
import PreInductionSection from './screens/PreInductionSection';
import MedicalStaffSection from './screens/MedicalStaffSection';
import IntubationMonitoringSection from './screens/IntubationMonitoringSection';
import ClinicalNotesSection from './screens/ClinicalNotesSection';

const AnesthesiaForm = () => {
  const { uhid_no } = useParams();
  const navigate = useNavigate();

  const {
    formData,
    setFormData,
    loading,
    signatureDialogOpen,
    setSignatureDialogOpen,
    signaturePadRef,
    drugGridRef,
    primaryMonitoringRef,
    advancedMonitoringRef,
    isUpdate,
    handleChange,
    handleNameChange,
    handleMonitoringChange,
    handleProcedureChange,
    handleDrugInputChange,
    handleSignatureOpen,
    handleSignatureSave,
    handleSubmit,
  } = useAnesthesiaForm(uhid_no, navigate);

  const handleBack = () => {
    navigate(`/mainPage/${uhid_no}`, {
      state: { from: 'PreOperativePhase', status: 'success', step: 1 },
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <p className="text-lg text-gray-600">Loading...</p>
      </div>
    );
  }

  return (
    <ThemeProvider theme={theme}>
      <div className="max-w-7xl mx-auto p-2 md:p-4 bg-gradient-to-br from-teal-50 to-white min-h-screen">
        <div className="bg-white shadow-xl rounded-xl p-3 md:p-6 border border-gray-200">
          <div className="flex justify-between items-center mb-4">
            <button
              type="button"
              onClick={handleBack}
              className="text-teal-600 font-bold px-4 py-2 hover:bg-teal-50 rounded-lg transition-colors border border-teal-600 flex items-center"
            >
              <span className="mr-2">&larr;</span> Back
            </button>
          </div>
          <div className="text-center mb-6">
            <h1 className="text-3xl md:text-4xl font-bold text-teal-800 mb-2">
              ANESTHESIA RECORD
            </h1>
            <div className="w-24 h-1 bg-teal-600 mx-auto rounded-full"></div>
          </div>

          <form onSubmit={handleSubmit}>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
              <PreInductionSection 
                formData={formData} 
                handleChange={handleChange} 
                handleNameChange={handleNameChange} 
                handleSignatureOpen={handleSignatureOpen}
              />

              <MedicalStaffSection formData={formData} handleChange={handleChange} />

              <IntubationMonitoringSection 
                formData={formData} 
                handleChange={handleChange} 
                handleMonitoringChange={handleMonitoringChange} 
                setFormData={setFormData}
              />
            </div>

            <hr className="my-8 border-gray-300" />

            <div className="space-y-8">
              <div>
                <div className="flex items-center mb-6">
                  <div className="w-3 h-8 bg-teal-600 rounded-full mr-4"></div>
                  <h2 className="text-2xl font-bold text-teal-700">Drug Administration Timeline</h2>
                </div>
                <DrugAdministrationGrid
                  ref={drugGridRef}
                  drugInputs={formData.drugInputs}
                  onDrugInputChange={handleDrugInputChange}
                  rows={8}
                  cols={30}
                />
              </div>

              <section className="mt-8">
                <div className="flex items-center mb-6">
                  <div className="w-3 h-8 bg-teal-600 rounded-full mr-4"></div>
                  <h2 className="text-2xl font-bold text-teal-700">Primary Vital Signs Monitoring</h2>
                </div>
                <PrimaryMonitoringGrid ref={primaryMonitoringRef} />
              </section>

              <section className="mt-8">
                <div className="flex items-center mb-6">
                  <div className="w-3 h-8 bg-teal-600 rounded-full mr-4"></div>
                  <h2 className="text-2xl font-bold text-teal-700">Advanced Monitoring Dashboard</h2>
                </div>
                <AdvancedMonitoringGrid ref={advancedMonitoringRef} />
              </section>

              <ClinicalNotesSection 
                formData={formData} 
                handleChange={handleChange} 
                handleProcedureChange={handleProcedureChange} 
              />
            </div>

            <div className="mt-12 pt-8 border-t-2 border-gray-200 text-center">
              <div className="bg-gradient-to-r from-teal-50 to-teal-50 rounded-xl p-6 mb-6">
                <h3 className="text-xl font-bold text-gray-800 mb-2">Record Completion Checklist</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                  <div className="flex items-center justify-center p-3 bg-white rounded-lg border">
                    <span className={`mr-2 ${formData.signature ? "text-teal-500" : "text-gray-400"}`}>
                      {formData.signature ? "✅" : "⭕"}
                    </span>
                    Digital Signature
                  </div>
                  <div className="flex items-center justify-center p-3 bg-white rounded-lg border">
                    <span className={`mr-2 ${formData.equipmentChecked ? "text-teal-500" : "text-gray-400"}`}>
                      {formData.equipmentChecked ? "✅" : "⭕"}
                    </span>
                    Equipment Check
                  </div>
                  <div className="flex items-center justify-center p-3 bg-white rounded-lg border">
                    <span className={`mr-2 ${formData.consentChecked ? "text-teal-500" : "text-gray-400"}`}>
                      {formData.consentChecked ? "✅" : "⭕"}
                    </span>
                    Consent Verified
                  </div>
                </div>
              </div>

              <div className="flex flex-wrap gap-3 justify-center mb-4">
                <DraftSaveButton
                  formKey={DRAFT_KEYS.ANESTHESIA_FORM}
                  uhidNo={uhid_no}
                  formData={formData}
                  onDraftLoaded={(draftData) => setFormData(prev => ({ ...prev, ...draftData }))}
                  variant="outlined"
                  size="large"
                  disabled={loading}
                  isUpdate={isUpdate}
                />
                <button
                  type="submit"
                  disabled={loading}
                  className="bg-gradient-to-r from-teal-600 to-teal-600 hover:from-teal-700 hover:to-teal-700 text-white font-bold py-4 px-12 rounded-xl shadow-xl transition-all duration-300 transform hover:scale-105 hover:shadow-2xl text-lg disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
                  aria-label="Submit Anesthesia Record"
                >
                  {loading ? "Submitting..." : (isUpdate ? "Update Anesthesia Record" : "Submit Complete Anesthesia Record")}
                </button>
              </div>
              <p className="text-sm text-gray-600 mt-3">
                All data will be securely stored and made available for medical record review
              </p>
            </div>
          </form>

          <SignatureDialog
            open={signatureDialogOpen}
            onClose={() => setSignatureDialogOpen(false)}
            onSave={handleSignatureSave}
            signaturePadRef={signaturePadRef}
          />
        </div>
      </div>
    </ThemeProvider>
  );
};

export default AnesthesiaForm;
